import { computed, type ComputedRef, type Ref } from 'vue'

import {
  getSceneBoundConsistencyAssets,
} from '@/views/customer/generation-workspace/consistencyAssets'
import {
  buildSceneVideoTaskDescription,
  buildSceneVideoTaskTimeline,
  formatSceneVideoTaskStatus,
  normalizeSceneVideoDuration,
} from '@/views/customer/generation-workspace/sceneVideo'
import type {
  ConsistencyAssetCollection,
  ConsistencyAssetDraft,
  StoryboardGenerationSceneNavigatorItem,
  StoryboardPreviewOverlayState,
  StoryboardSceneDraft,
  UploadedWorkspaceVideo,
} from '@/views/customer/generation-workspace/types'
import type { SceneImageAction } from '@/views/customer/generation-workspace/useSceneImageWorkspace'

interface UseStoryboardStageViewStateOptions {
  storyboardDraftScenes: Ref<StoryboardSceneDraft[]>
  storyboardScenes: ComputedRef<StoryboardSceneDraft[]>
  selectedStoryboardSceneNumber: Ref<number | null>
  hoveredStoryboardSceneNumber: Ref<number | null>
  consistencyAssets: Ref<ConsistencyAssetCollection>
  uploadedSceneVideoMap: ComputedRef<Record<number, UploadedWorkspaceVideo>>
  generatedSceneVideoMap: ComputedRef<Record<number, Record<string, any>>>
  sceneVideoTaskMap: ComputedRef<Record<number, Record<string, any>>>
  sceneVideoPlayerErrors: Ref<Record<number, string>>
  storyboardGenerationTab: Ref<'image' | 'video'>
  sceneImageLoading: Ref<Record<number, SceneImageAction | null>>
  sceneVideoLoading: Ref<Record<number, boolean>>
  sceneVideoGenerating: Ref<Record<number, boolean>>
  trackedSceneVideoTaskIds: Ref<Record<number, string>>
  getSceneVideoTaskStatus: (sceneNumber: number) => string
  isSceneVideoTaskActive: (sceneNumber: number) => boolean
  buildSceneImageOverlayState: (action: SceneImageAction | null) => StoryboardPreviewOverlayState | null
}

export function useStoryboardStageViewState(options: UseStoryboardStageViewStateOptions) {
  function getSceneNavigatorTitle(scene: StoryboardSceneDraft) {
    const candidates = [scene.location, scene.title, scene.action, scene.description]
      .map((item) => String(item || '').trim())
      .filter(Boolean)
    return candidates[0] || `镜头 ${scene.scene_number}`
  }

  const selectedStoryboardSceneDraft = computed(() => {
    if (!options.storyboardDraftScenes.value.length) {
      return null
    }

    return options.storyboardDraftScenes.value.find((scene) => scene.scene_number === options.selectedStoryboardSceneNumber.value)
      || options.storyboardDraftScenes.value[0]
  })

  const selectedStoryboardBoundAssets = computed(() => {
    const scene = selectedStoryboardSceneDraft.value
    if (!scene) {
      return {
        character: [] as ConsistencyAssetDraft[],
        scene: [] as ConsistencyAssetDraft[],
        prop: [] as ConsistencyAssetDraft[],
      }
    }

    return {
      character: getSceneBoundConsistencyAssets(scene, 'character', options.consistencyAssets.value),
      scene: getSceneBoundConsistencyAssets(scene, 'scene', options.consistencyAssets.value),
      prop: getSceneBoundConsistencyAssets(scene, 'prop', options.consistencyAssets.value),
    }
  })

  const storyboardGenerationSceneItems = computed<StoryboardGenerationSceneNavigatorItem[]>(() => options.storyboardDraftScenes.value.map((scene) => {
    const sceneNumber = scene.scene_number
    return {
      scene_number: sceneNumber,
      title: getSceneNavigatorTitle(scene),
      image_ready: Boolean(String(scene.background_image_url || '').trim()),
      video_ready: Boolean(
        String(options.uploadedSceneVideoMap.value[sceneNumber]?.url || options.generatedSceneVideoMap.value[sceneNumber]?.video_url || '').trim()
      ),
    }
  }))

  const storyboardVisualActiveSceneNumber = computed(() => options.hoveredStoryboardSceneNumber.value || options.selectedStoryboardSceneNumber.value)
  const selectedStoryboardImageUrl = computed(() => String(selectedStoryboardSceneDraft.value?.background_image_url || '').trim())
  const selectedStoryboardUploadedVideo = computed(() => {
    const sceneNumber = options.selectedStoryboardSceneNumber.value
    return sceneNumber ? options.uploadedSceneVideoMap.value[sceneNumber] || null : null
  })
  const selectedStoryboardVideoUrl = computed(() => {
    const sceneNumber = options.selectedStoryboardSceneNumber.value
    if (!sceneNumber) {
      return ''
    }
    return String(options.uploadedSceneVideoMap.value[sceneNumber]?.url || options.generatedSceneVideoMap.value[sceneNumber]?.video_url || '').trim()
  })
  const selectedSceneVideoTask = computed<Record<string, any> | null>(() => {
    const sceneNumber = options.selectedStoryboardSceneNumber.value
    return sceneNumber ? options.sceneVideoTaskMap.value[sceneNumber] || null : null
  })
  const selectedStoryboardVideoError = computed(() => {
    const sceneNumber = options.selectedStoryboardSceneNumber.value
    return sceneNumber ? options.sceneVideoPlayerErrors.value[sceneNumber] || '' : ''
  })
  const selectedSceneVideoTaskStatusLabel = computed(() => formatSceneVideoTaskStatus(String(selectedSceneVideoTask.value?.status || '')))
  const selectedSceneVideoTaskDescription = computed(() => buildSceneVideoTaskDescription(selectedSceneVideoTask.value))
  const selectedSceneVideoTaskTimeline = computed(() => buildSceneVideoTaskTimeline(selectedSceneVideoTask.value))
  const selectedSceneVideoTaskFacts = computed(() => {
    const task = selectedSceneVideoTask.value
    if (!task) {
      return [] as Array<{ label: string; value: string }>
    }

    return [
      { label: '任务状态', value: selectedSceneVideoTaskStatusLabel.value },
      { label: '视频时长', value: `${normalizeSceneVideoDuration(task.duration || 5, 5)} 秒` },
      { label: '任务编号', value: String(task.provider_task_id || task.task_id || '待返回') },
    ]
  })
  const selectedScenePreviewOverlay = computed<StoryboardPreviewOverlayState | null>(() => {
    const sceneNumber = options.selectedStoryboardSceneNumber.value
    if (!sceneNumber) {
      return null
    }

    if (options.storyboardGenerationTab.value === 'image') {
      return options.buildSceneImageOverlayState(options.sceneImageLoading.value[sceneNumber] || null)
    }

    if (options.sceneVideoLoading.value[sceneNumber]) {
      return {
        title: '视频上传中',
        description: '正在上传人工处理视频，并归档到当前镜头。',
        note: '上传完成后会立即替换当前镜头预览。',
      }
    }

    if (options.sceneVideoGenerating.value[sceneNumber]) {
      return {
        title: '视频任务提交中',
        description: '正在提交当前镜头图片、时长和提示词到视频模型。',
        note: '任务建立成功后会自动开始轮询进度。',
      }
    }

    const task = options.sceneVideoTaskMap.value[sceneNumber] || null
    const status = String(task?.status || '').trim().toLowerCase()
    if (status === 'queued' || status === 'running') {
      return {
        title: status === 'queued' ? '视频排队中' : '视频生成中',
        description: buildSceneVideoTaskDescription(task),
        note: '任务完成后会自动回填当前镜头，并归档到素材中心。',
      }
    }

    if (options.trackedSceneVideoTaskIds.value[sceneNumber] && !task) {
      return {
        title: '视频进度同步中',
        description: '任务已经提交，正在同步最新进度。',
        note: '拿到状态后会自动刷新当前镜头预览。',
      }
    }

    return null
  })

  return {
    selectedScenePreviewOverlay,
    selectedScenePreviewOverlayDescription: computed(() => selectedScenePreviewOverlay.value?.description || ''),
    selectedScenePreviewOverlayNote: computed(() => selectedScenePreviewOverlay.value?.note || ''),
    selectedScenePreviewOverlayTitle: computed(() => selectedScenePreviewOverlay.value?.title || ''),
    selectedScenePreviewOverlayVisible: computed(() => Boolean(selectedScenePreviewOverlay.value)),
    selectedSceneVideoTask,
    selectedSceneVideoTaskDescription,
    selectedSceneVideoTaskFacts,
    selectedSceneVideoTaskStatusLabel,
    selectedSceneVideoTaskTimeline,
    storyboardGenerationSceneItems,
    selectedStoryboardBoundAssets,
    selectedStoryboardImageUrl,
    selectedStoryboardSceneDraft,
    selectedStoryboardUploadedVideo,
    selectedStoryboardVideoError,
    selectedStoryboardVideoUrl,
    storyboardVisualActiveSceneNumber,
  }
}